import { register } from "./registry";
import { moveFocus } from "./keyboard";
import { toast } from "./toast";

type Item = { id: string; text: string; secondary?: string; code?: string };

/**
 * Holds every key typed while an Enter-lookup is in flight and replays it, in order, into whatever field has focus once the lookup
 * has moved on. A key that starts another lookup during the replay is held again by that lookup, so the order is kept.
 */
function holdKeys(): () => void {
  const held: { key: string; shiftKey: boolean; ctrlKey: boolean }[] = [];
  const onKey = (e: KeyboardEvent) => {
    // Save (Ctrl+S / F10) is held too: fired at once it would save before the typed-ahead fields are filled.
    const save = e.key === "F10" || (e.ctrlKey && (e.key === "s" || e.key === "S"));
    if (!save && (e.ctrlKey || e.altKey || e.metaKey)) return;
    if (!save && e.key.length !== 1 && e.key !== "Enter" && e.key !== "Tab" && e.key !== "Backspace") return;
    e.preventDefault(); e.stopImmediatePropagation();
    held.push({ key: e.key, shiftKey: e.shiftKey, ctrlKey: e.ctrlKey });
  };
  window.addEventListener("keydown", onKey, true);
  return () => {
    window.removeEventListener("keydown", onKey, true);
    let typed = ""; let typedIn: HTMLElement | null = null;
    for (const k of held) {
      const field = document.activeElement as HTMLElement | null;
      if (!field) break;
      if (!field.dispatchEvent(new KeyboardEvent("keydown", { key: k.key, shiftKey: k.shiftKey, ctrlKey: k.ctrlKey, bubbles: true, cancelable: true }))) continue;
      if (field instanceof HTMLSelectElement && k.key.length === 1 && !k.ctrlKey) {
        // A replayed key has no default action, so do the select's type-to-pick here (typed "Bilti" picks "Bilti Freight").
        if (typedIn !== field) { typed = ""; typedIn = field; }
        typed += k.key.toLowerCase();
        const hit = Array.from(field.options).find(o => o.text.toLowerCase().startsWith(typed));
        if (hit && field.value !== hit.value) { field.value = hit.value; field.dispatchEvent(new Event("change", { bubbles: true })); }
        continue;
      }
      if (k.ctrlKey || !(field instanceof HTMLInputElement) || field.readOnly) continue;
      if (k.key.length === 1) field.setRangeText(k.key, field.selectionStart ?? field.value.length, field.selectionEnd ?? field.value.length, "end");
      else if (k.key === "Backspace") field.value = field.value.slice(0, -1);
      else continue;
      field.dispatchEvent(new Event("input", { bubbles: true }));
    }
  };
}

register((root) => {
    const inputs = root instanceof HTMLInputElement && root.matches("input[data-kit-ac]")
      ? [root]
      : Array.from(root.querySelectorAll<HTMLInputElement>("input[data-kit-ac]"));
    inputs.forEach((input) => {
    if (input.dataset.kitBound === "1") return;
    input.dataset.kitBound = "1";
    input.dataset.kitOwnsEnter = "1";
    input.setAttribute("autocomplete", "off");
    const getTarget = () => {
      if (!input.dataset.kitAcTarget) return null;
      const targetName = input.dataset.kitAcTarget;
      const propName = targetName.includes(".") ? targetName.split(".").pop() : targetName;
      return input.closest("tr")?.querySelector<HTMLInputElement>(`input[name='${targetName}'], input[name$='.${propName}']`)
        ?? input.form?.querySelector<HTMLInputElement>(`input[name='${targetName}']`);
    };
    // The value the page was opened for; typing blanks the hidden id, so compare picks against this.
    const shownId = getTarget()?.value ?? "";
    const list = document.createElement("ul"); list.className = "kit-ac-list"; list.hidden = true; list.setAttribute("role", "listbox");
    input.after(list);
    let items: Item[] = []; let sel = 0; let timer = 0; let seq = 0;

    const render = () => {
      list.replaceChildren(...items.map((it, i) => {
        const li = document.createElement("li"); li.setAttribute("role", "option"); li.setAttribute("aria-selected", String(i === sel));
        li.textContent = it.text;
        if (it.secondary) { const s = document.createElement("small"); s.textContent = it.secondary; li.append(s); }
        li.addEventListener("mousedown", (e) => {
          if (e.button !== 0) return;
          e.preventDefault(); // keep focus on the field while committing the same ID/change events as Enter
          accept(it);
          input.focus();
        });
        return li;
      }));
      list.hidden = items.length === 0;
    };
    let isDirty = false;
    const accept = (it?: Item) => {
      isDirty = false;
      if (!it) return false;
      input.removeAttribute("aria-invalid");
      ++seq;
      window.clearTimeout(timer);
      input.value = it.text;
      const target = getTarget();
      // A hidden id that asks for it (hx-trigger, e.g. the sub-ledger box, a manifest's truck details) hears the pick too.
      if (target) { target.value = it.id; if (target !== input && target.hasAttribute("hx-trigger")) target.dispatchEvent(new Event("change", { bubbles: true })); }
      // data-kit-ac-secondary="LicenceNo": also show the picked row's secondary text (e.g. a driver's licence no.).
      const secondaryName = input.dataset.kitAcSecondary;
      if (secondaryName) {
        const box = (input.form ?? document).querySelector<HTMLInputElement>(`[name="${secondaryName}"], #${CSS.escape(secondaryName)}`);
        if (box) box.value = it.secondary ?? "";
      }
      items = []; render();
      input.dispatchEvent(new CustomEvent("kit:accepted", { bubbles: true, detail: it }));
      input.dispatchEvent(new Event("change", { bubbles: true }));
      // data-kit-ac-reload="branch_id": picking a value reloads the page for it (e.g. another branch's openings).
      const reload = input.dataset.kitAcReload;
      if (reload && shownId !== it.id) {
        const url = new URL(location.href);
        url.searchParams.set(reload, it.id);
        location.assign(url);
      }
      return true;
    };
    // quick-create.ts: a record just added with F2 is picked like a chosen row.
    input.addEventListener("kit:ac-accept", (e) => accept((e as CustomEvent<Item>).detail));
    const moveNext = () => {
      if (input.hasAttribute("data-kit-submit-on-enter")) {
        const form = input.form ?? input.closest("form");
        if (form) form.requestSubmit();
        return;
      }
      const grid = input.closest<HTMLTableElement>("table[data-kit-grid]");
      if (grid) {
        input.dispatchEvent(new CustomEvent("kit:enter", { bubbles: true }));
        return;
      }
      moveFocus(input, 1);
    };
    const buildUrl = (q: string) => {
      const base = input.dataset.kitAc!;
      const url = new URL(base, window.location.origin);
      url.searchParams.set("q", q);
      if (!url.searchParams.has("limit")) {
        url.searchParams.set("limit", "20");
      }
      for (const attr of Array.from(input.attributes)) {
        if (attr.name.startsWith("data-kit-ac-param-")) {
          const paramName = attr.name.slice("data-kit-ac-param-".length);
          const targetName = attr.value;
          const propName = targetName.includes(".") ? targetName.split(".").pop()! : targetName;
          const paramInput = input.closest("tr")?.querySelector<HTMLInputElement>(`input[name='${targetName}'], input[name$='.${propName}']`)
            ?? input.form?.querySelector<HTMLInputElement>(`input[name='${targetName}'], select[name='${targetName}'], input[name$='.${propName}']`)
            ?? document.querySelector<HTMLInputElement>(`input[name='${targetName}'], input[name$='.${propName}'], #${targetName}`);
          if (paramInput && paramInput.value) {
            url.searchParams.set(paramName, paramInput.value);
          }
        }
      }
      return url.pathname + url.search;
    };

    input.addEventListener("input", () => {
      isDirty = true;
      input.removeAttribute("aria-invalid");
      const mine = ++seq;
      items = []; render();
      const target = getTarget();
      if (target && target !== input) target.value = "";
      window.clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 1) { items = []; render(); return; }
      const endpoint = buildUrl(q);
      timer = window.setTimeout(async () => {
        const r = await fetch(endpoint);
        if (mine !== seq || !r.ok) return;
        const found = await r.json() as Item[];
        if (mine !== seq) return;
        items = found; sel = 0; render();
      }, 80);
    });
    input.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "ArrowDown" && items.length) { e.preventDefault(); sel = Math.min(sel + 1, items.length - 1); render(); return; }
      if (e.key === "ArrowUp" && items.length) { e.preventDefault(); sel = Math.max(sel - 1, 0); render(); return; }
      if (e.key === "Escape" && !list.hidden) { e.preventDefault(); e.stopPropagation(); items = []; render(); return; }
      if (e.key === "F2") { input.dispatchEvent(new CustomEvent("kit:quick-create", { bubbles: true, detail: { resource: input.dataset.kitAc } })); return; }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) { moveFocus(input, -1); return; }
        if (items.length) { accept(items[sel]); moveNext(); return; }
        if (!isDirty) { moveNext(); return; }
        const q = input.value.trim();
        if (!q) { isDirty = false; moveNext(); return; }
        const endpoint = buildUrl(q);
        window.clearTimeout(timer);
        const mine = ++seq;
        // A fast typist keeps typing while this lookup runs: hold those keys and replay them in the next field (as the C.N. grid does).
        const release = holdKeys();
        fetch(endpoint)
          .then(r => r.ok ? r.json() as Promise<Item[]> : [])
          .then(found => {
            if (mine !== seq) return;
            if (found.length) accept(found[0]);
            // owner 2026-10-30: data-kit-ac-strict keeps an unmatched name in its field instead of moving on (it was refused only at save)
            else if (input.hasAttribute("data-kit-ac-strict")) { input.setAttribute("aria-invalid", "true"); toast("error", `Nothing matches "${q}". Pick from the list or press F2 to add it.`); return; }
            moveNext();
          })
          .catch(() => { if (mine === seq) moveNext(); })
          .finally(release);
      }
    });
  });
});
